import { PencilIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { Link } from "react-router-dom";

interface ExerciseProgressionCardProps {
  exerciseSession: ExerciseWithParentWorkout;
  selectedWorkout: string;
  selectedGraphType: "avg-weight" | "avg-reps" | "avg-volume" | "total-volume";
  lastItem?: boolean;
  onClick: (workoutId: string) => void;
}

const ExerciseProgressionCard: React.FC<ExerciseProgressionCardProps> = ({
  exerciseSession,
  selectedWorkout,
  selectedGraphType,
  lastItem,
  onClick,
}) => {
  const selected = exerciseSession.parentWorkout.id === selectedWorkout;

  const workoutDate = new Date(exerciseSession.parentWorkout.date);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const formattedDate = workoutDate.toLocaleDateString("en-US", options);

  const getSetText = (set: ExerciseSet, index: number): string => {
    return `Set ${index + 1}: ${set.weight || "0"}x${set.reps || "0"}`;
  };

  let totalVolume = 0;
  let totalWeight = 0;
  let totalReps = 0;
  exerciseSession.sets.forEach((set) => {
    totalVolume +=
      parseFloat(set.weight?.toString() || "0") *
      parseFloat(set.reps?.toString() || "0");
    totalWeight += parseFloat(set.weight?.toString() || "0");
    totalReps += parseFloat(set.reps?.toString() || "0");
  });

  const averageWeight =
    Math.round(
      (totalWeight / exerciseSession.sets.length + Number.EPSILON) * 100
    ) / 100;
  const averageReps =
    Math.round(
      (totalReps / exerciseSession.sets.length + Number.EPSILON) * 100
    ) / 100;
  const averageVolume =
    Math.round(
      (totalVolume / exerciseSession.sets.length + Number.EPSILON) * 100
    ) / 100;

  const handleCardClicked = (workoutId: string) => {
    onClick(workoutId);
  };

  return (
    <div
      id={`progression-card-${exerciseSession.parentWorkout.id}`}
      key={exerciseSession._id}
      className={classNames("grid gap-x-2 grid-cols-[12px,1fr]", {
        "mb-2": lastItem,
      })}
      onClick={() => handleCardClicked(exerciseSession.parentWorkout.id)}
    >
      <div className="w-full flex flex-col items-center h-[calc(100%+31px)]">
        {selected ? (
          <div className="mt-[3px] rounded-full border-2 border-white bg-indigo-500 w-[12px] h-[12px]"></div>
        ) : (
          <div className="mt-[7px] rounded-full border border-indigo-500 w-[7px] h-[7px]"></div>
        )}
        {!lastItem && <div className="w-[1px] h-full bg-indigo-500"></div>}
      </div>
      <div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 w-full mb-1">
          <div className="w-full flex gap-x-2">
            <h2
              className={classNames(
                "text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis",
                {
                  "text-gray-900": !selected,
                  "text-indigo-600": selected,
                }
              )}
            >
              {exerciseSession.parentWorkout.title || "Untitled Exercise"}
            </h2>
            <Link
              to={`/workout/${exerciseSession.parentWorkout.id}/exercise/${exerciseSession._id}`}
              className="inline-flex items-center"
              aria-label="View exercise details"
              onClick={(e) => e.stopPropagation()}
            >
              <PencilIcon
                className="h-[14px] w-auto text-indigo-500 mb-[2px]"
                aria-hidden="true"
              />
            </Link>
          </div>
          <p
            className={classNames(
              "text-xs w-full whitespace-nowrap overflow-hidden text-ellipsis",
              { "text-gray-500": !selected, "text-indigo-500": selected }
            )}
          >
            {formattedDate}
          </p>
          {exerciseSession?.sets &&
            exerciseSession.sets.map((set, index) => {
              return (
                <p key={index + 1} className="text-sm text-gray-700">
                  {getSetText(set, index)}
                </p>
              );
            })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 w-full">
          {selectedGraphType === "avg-weight" && (
            <p
              className={classNames("text-sm font-bold", {
                "text-gray-700":
                  !selected || selectedGraphType !== "avg-weight",
                "text-indigo-600":
                  selected && selectedGraphType === "avg-weight",
              })}
            >
              Avg Weight: {averageWeight}
            </p>
          )}
          {selectedGraphType === "avg-reps" && (
            <p
              className={classNames("text-sm font-bold", {
                "text-gray-700": !selected || selectedGraphType !== "avg-reps",
                "text-indigo-600": selected && selectedGraphType === "avg-reps",
              })}
            >
              Avg Reps: {averageReps}
            </p>
          )}

          {selectedGraphType === "avg-volume" && (
            <p
              className={classNames("text-sm font-bold", {
                "text-gray-700":
                  !selected || selectedGraphType !== "avg-volume",
                "text-indigo-600":
                  selected && selectedGraphType === "avg-volume",
              })}
            >
              Avg Volume: {averageVolume}
            </p>
          )}

          {selectedGraphType === "total-volume" && (
            <p
              className={classNames("text-sm font-bold", {
                "text-gray-700":
                  !selected || selectedGraphType !== "total-volume",
                "text-indigo-600":
                  selected && selectedGraphType === "total-volume",
              })}
            >
              Total Volume: {totalVolume}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseProgressionCard;
